import React, { useEffect, useState, useCallback } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { ShieldCheck } from '@phosphor-icons/react';
import useApi from '../../../utils/AxiosWrapper';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../../utils/UserContext';
import { setApplicationStep } from '../../../pages/signup';
import {
  ApplicationStatus,
  ApplicationStep,
  Entity,
} from '../../../types/apiTypes';
import { ClipLoader } from 'react-spinners';
import { useScreenView } from '../../../utils/ScreenViewContext';
import toast from 'react-hot-toast';
import { APPROVAL_BASIS } from '../../../constants/approvalBasis';
import { Application } from '../../../types/dbTypes';
import SkipPlaidModal from './SkipPlaidModal';
import PlaidInfoSection from './PlaidInfoSection';
import TrustSignals from '../TrustSignals';
import useFrontChat from '../../../hooks/useFrontChat';
interface PlaidLinkProps {
  currStep: string;
  selectedRole: Entity;
  setStep: (step: string) => void;
  application_id: string;
  application: Application;
  setLoading: (isLoading: boolean) => void;
  loading: boolean;
}

function PlaidLink({
  currStep,
  selectedRole,
  setStep,
  application_id,
  application,
  setLoading,
  loading,
}: PlaidLinkProps) {
  // Initialize Front chat widget
  useFrontChat();

  const { callProtectedApi } = useApi();
  const navigate = useNavigate();
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [openLink, setOpenLink] = useState(false);
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [isPlaidInfoExpanded, setIsPlaidInfoExpanded] = useState(false);
  const { userRecord, userId } = useUser();
  const { screenView } = useScreenView();

  useEffect(() => {
    const fetchLinkToken = async (customer_id: string) => {
      setLoading(true);
      try {
        const body = {
          owner_id: customer_id,
          entity: 'customer', // Get curr (Customer, Vendor, or Partner)
        };
        const response = await callProtectedApi(
          '/linkedAccounts/plaidLinkToken',
          'POST',
          body,
          'Error occurred while connecting to Plaid.',
          undefined,
          undefined,
          5
        );
        if (response?.data?.data?.token) {
          setLinkToken(response.data.data.token);
        }
      } catch (error: any) {
        console.error('Error fetching Plaid link token:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchLinkToken(
      userRecord?.userType === Entity.Customer ? userRecord?.customer_id : ''
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createLinkedAccount = async (
    customer_id: string,
    publicToken: string
  ) => {
    setLoading(true);
    try {
      const body = {
        public_token: publicToken,
        entity: 'customer',
      };
      const response = await callProtectedApi(
        `/linkedAccounts/${customer_id}`,
        'POST',
        body,
        'Error linking your account with Plaid.',
        undefined,
        undefined,
        5
      );
      if (response?.status === 200 || response?.status === 201) {
        return true;
      }
    } catch (error: any) {
      console.error('Error fetching creating linked account:', error);
    }
    return false;
  };

  const handleSetLoading = (loading: boolean) => {
    setLoading(loading);
  };

  // "Continue with statements": advance plaid_link -> manual_document_upload. The backend
  // (handleManualDocumentUploadStep, gated on the persisted plaid_link step) auto-creates the
  // bank-statement document request and marks the application AWAITING_DOCUMENT_REQUEST, dropping the
  // customer onto the existing upload flow. No status is sent — the BE derives it.
  const handleSubmitStatements = async () => {
    setLoading(true);
    setShowSkipModal(false);
    try {
      const step = await setApplicationStep({
        step: ApplicationStep.ManualDocumentUpload,
        application_id,
        customer_id: userId ?? '',
        callProtectedApi,
        setLoading: handleSetLoading,
        previousStep: ApplicationStep.PlaidLink,
      });

      if (step === ApplicationStep.ManualDocumentUpload) {
        setStep(ApplicationStep.ManualDocumentUpload);
      }
    } catch (error) {
      toast.error('Something went wrong. Please contact TACH support.');
    } finally {
      setLoading(false);
    }
  };

  const onSuccess = useCallback(
    async (public_token: string) => {
      try {
        const success = await createLinkedAccount(userId ?? '', public_token);
        if (success) {
          const step = await setApplicationStep({
            step: ApplicationStep.PendingReview,
            application_id,
            customer_id: userId ?? '',
            callProtectedApi,
            setLoading: handleSetLoading,
            previousStep: ApplicationStep.PlaidLink,
            applicationStatus: application?.status as ApplicationStatus,
          });
          if (step?.length > 0 && step === ApplicationStep.PendingReview) {
            setStep(ApplicationStep.PendingReview);
          }
        } else {
          toast.error(
            'Error linking your account with Plaid. Please contact TACH support.'
          );
        }
      } catch (error: any) {
        console.error('Error adding payment method:', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [callProtectedApi, navigate]
  );

  const { open, ready } = usePlaidLink({
    token: linkToken!,
    onSuccess,
  });

  useEffect(() => {
    if (openLink && ready) {
      open();
      setOpenLink(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openLink, ready, open]);

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="flex h-full w-full flex-col items-center">
            {!loading ? (
              <div className="w-full flex-1 overflow-y-auto py-4 md:py-10 lg:overflow-visible lg:py-20">
                <div className="flex w-full justify-center px-6 lg:px-12">
                  <div className="mx-auto flex w-auto flex-col items-center justify-center gap-8 lg:flex-row lg:items-start">
                    {/* Form section */}
                    <div className="mx-auto flex-1 lg:w-[800px] lg:max-w-[800px]">
                      <div className="flex flex-col items-center">
                        <h2 className="justify-center text-center text-[32px] font-semibold text-primary">
                          Verify Your Business Revenue
                        </h2>
                      </div>

                      {/* Positive framing — a benefit, not a warning */}
                      <div className="mx-auto mt-6 flex max-w-[650px] items-start gap-3 rounded-lg border border-[#6A9E68]/40 bg-[#F0F5F0] p-4">
                        <ShieldCheck
                          size={24}
                          weight="fill"
                          className="mt-0.5 flex-shrink-0 text-[#6A9E68]"
                        />
                        <p className="text-left text-sm text-gray-700 md:text-base">
                          <span className="font-semibold text-primary">
                            {APPROVAL_BASIS.lead}
                          </span>{' '}
                          {APPROVAL_BASIS.revenueStepBody}
                        </p>
                      </div>

                      {/* Two verification options */}
                      <div className="mx-auto mt-8 grid w-full max-w-[720px] grid-cols-1 gap-4 md:grid-cols-2">
                        {/* Option A — Link with Plaid (instant) */}
                        <div className="flex flex-col rounded-xl border-2 border-[#6A9E68] bg-white p-6 shadow-sm">
                          <span
                            id="plaid-option-badge"
                            className="mb-3 inline-flex w-fit items-center rounded-full bg-[#4E7B4C] px-3 py-1 text-xs font-semibold text-white"
                          >
                            Instant · Recommended
                          </span>
                          <h3 className="mb-2 text-xl font-bold text-primary">
                            Link with Plaid
                          </h3>
                          <p
                            id="plaid-option-desc"
                            className="mb-4 flex-1 text-sm text-gray-600"
                          >
                            Securely connect the account where you receive{' '}
                            <b className="underline">direct deposit</b> payments
                            from your factoring company, 3PL, or shippers. Takes
                            1–2 minutes and gets you approved fastest.
                          </p>
                          <button
                            onClick={() => setOpenLink(true)}
                            disabled={!linkToken}
                            aria-describedby="plaid-option-badge plaid-option-desc"
                            className="flex w-full items-center justify-center gap-2 rounded-md border border-transparent bg-black px-4 py-3 font-medium text-white hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 enabled:cursor-pointer disabled:opacity-30"
                          >
                            <img alt="" src="/plaid.svg" className="h-8 w-8" />
                            <span className="text-lg font-bold text-white">
                              Link Instantly
                            </span>
                          </button>
                        </div>

                        {/* Option B — Submit bank statements */}
                        <div className="flex flex-col rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                          <span
                            id="statements-option-badge"
                            className="mb-3 inline-flex w-fit items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600"
                          >
                            Manual
                          </span>
                          <h3 className="mb-2 text-xl font-bold text-primary">
                            Submit 6 Months of Bank Statements
                          </h3>
                          <p
                            id="statements-option-desc"
                            className="mb-4 flex-1 text-sm text-gray-600"
                          >
                            Prefer not to link? We'll verify your revenue from
                            your last 6 months of business bank statements. A
                            TACH specialist will reach out to collect them.
                          </p>
                          <button
                            onClick={() => setShowSkipModal(true)}
                            aria-describedby="statements-option-badge statements-option-desc"
                            className="w-full rounded-md border border-gray-300 bg-white px-4 py-3 text-lg font-semibold text-primary transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
                          >
                            Submit Bank Statements
                          </button>
                        </div>
                      </div>

                      {/* What is Plaid? — full width below the two options */}
                      <div className="mx-auto mt-6 w-full max-w-[720px]">
                        <PlaidInfoSection
                          isExpanded={isPlaidInfoExpanded}
                          onToggle={() =>
                            setIsPlaidInfoExpanded(!isPlaidInfoExpanded)
                          }
                        />
                      </div>
                    </div>

                    {/* Desktop sidebar with trust signals */}
                    {screenView === 'desktop' ? (
                      <div className="sticky top-8 hidden h-fit w-[350px] lg:block">
                        <TrustSignals variant="sidebar" />
                      </div>
                    ) : (
                      <div className="mb-4 w-full max-w-[600px]">
                        <TrustSignals variant="mobile" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <ClipLoader
                  color="#1D213B"
                  loading={loading}
                  size={60}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="flex h-full w-full justify-center"
                />
              </div>
            )}
            {/* Submit-statements confirmation (steers back toward Plaid) */}
            <SkipPlaidModal
              isOpen={showSkipModal}
              onCancel={() => setShowSkipModal(false)}
              onConfirm={handleSubmitStatements}
              onLinkPlaid={() => {
                setShowSkipModal(false);
                setOpenLink(true);
              }}
            />
          </div>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default PlaidLink;
